/*
 * @author: wayou
 * @date: 2018-12-13 13:15:56
 * @description: ai 报告中关键词数据
 */

import { fetch } from '@umu/umu-fetch';
import { IHomeworkInfo, KEYWORD_TASK_STATUS, HomeworkType, EAiHomeWorkStatus } from '@umu/umu-types';
import { IAiReportData } from '@umu/umu-types/src/interface/aiReportInfo';
export { HomeworkType as homeworkTypeMap } from '@umu/umu-types';

export interface IGetHomeworkKeywordsRequest {
    homework_id: number | string;
    student_id: string;
    return_text: number;
}

export enum InsightStatus {
    CLOSE = 0,
    WAITING = 1,
    RUNNING = 2,
    DONE = 3,
}

export interface AiInsights {
    // 整体评价
    overall_comment?: string;
    // 1 待执行 2 执行中 3 执行完成
    overall_comment_status?: InsightStatus;
    // 关键词评价
    keyword_comment?: string;
    // 1 待执行 2 执行中 3 执行完成
    keyword_comment_status?: InsightStatus;
    // 1 待执行 2 执行中 3 执行完成
    topic_identification_status?: InsightStatus;
    //  [     {         "start": 0,         "end": 10000,         "topic_name": "开场问候",         "speaker_id": "0"     } ]
    topic_identification_url?: string;
    // {     "大家好，我是蔡雨欣。": "开场问候" }
    topic_identification?: string;
    // 表现力评价
    expressiveness_comment?: string;
    // 1 待执行 2 执行中 3 执行完成
    expressiveness_comment_status?: InsightStatus;
    // 1 待执行 2 执行中 3 执行完成
    insights_status?: InsightStatus;
    // 关键词时间点详情[     {         "start": 0,         "end": 10000,         "keyword_name": "开场问候",         "speaker_id": "0"     } ]
    keywords_detail_url?: string;
    // 1 待执行 2 执行中 3 执行完成
    keywords_detail_status?: InsightStatus;
}

export interface IAiHighlights {
    key: string;
    content: string[];
}

export interface ITotalScoreDetailItem {
    score: number;
    full_score: number;
}

export interface ITotalScoreDetail {
    expressive: ITotalScoreDetailItem;
    keyword: ITotalScoreDetailItem;
    presentation: ITotalScoreDetailItem;
}

export interface IGetKeywordsResponse {
    ai_shield_highlights?: IAiHighlights[];
    ai_highlights?: IAiHighlights[];
    keywords_task_status: KEYWORD_TASK_STATUS;
    total_score?: number;
    total_score_status?: EAiHomeWorkStatus;
    total_score_detail?: ITotalScoreDetail;
    keywords_detail_status?: EAiHomeWorkStatus;
    keywords_detail_url?: string;
    keywords: {
        word_num: number;
        keyword_coverage: number;
        list: IKeywordItem[];
        speaker_keywords?: {
            speaker_id: string;
            list: IKeywordItem[];
            score: number;
        }[];
    };
    shield_keywords: {
        word_num: number;
        keyword_coverage: number;
        list: IKeywordItem[];
        speaker_keywords?: {
            speaker_id: string;
            list: IKeywordItem[];
            score: number;
        }[];
    };
    frequency_words: {
        word_num: number;
        list: IKeywordItem[];
    };
    show_keyword_coverage: number; // 0 不展示 1 展示
    text: string;
    ai_report_info?: IAiReportData;
    user_info: IUserInfo;
    homework_info: IHomeworkInfo & {
        expressiveness_task_status: number;
    };
    homework_type: HomeworkType;
    highlight: string[]; // 关键词高亮
    mantra_highlights: string[]; // 口头禅高亮
    shield_highlight_keywords: string[]; //屏蔽关键词高亮
    asr_url?: string;
    switch: {
        pause_switch: boolean;
        speed_switch: boolean;
        ai_voice_check_switch: boolean;
    };
    mantra_status: string;
    mantra_asr_url: string;
    insights?: AiInsights;
}

export { EAiHomeWorkStatus };

export const getHomeworkKeywords = (params: IGetHomeworkKeywordsRequest): Promise<IResponse<IGetKeywordsResponse>> => {
    return fetch.get('/uapi/v1/homework/ai-report', params);
};

export interface IPollConfig {
    intervalMs?: number;
    maxPollCount?: number;
}

export const pollHomeworkKeywords = async (
    params: IGetHomeworkKeywordsRequest,
    pollConfig?: IPollConfig,
): Promise<IResponse<IGetKeywordsResponse> | null> => {
    const { intervalMs = 2000, maxPollCount = 1800 } = pollConfig || {};
    let pollCount = 0;
    while (true) {
        pollCount++;
        if (pollCount > maxPollCount) {
            throw new Error('poll count exceed maxPollCount');
        }

        const res = await getHomeworkKeywords(params);
        if (res.error_code !== 0) {
            // 现在有一个情况是如果在 poll 过程中，用户又返回录制中，此时再 poll 会返回错误，这时候直接返回 null
            // 现在的错误码是 30002，先这样处理吧
            return null;
        }
        if (res.data.keywords_task_status == KEYWORD_TASK_STATUS.DONE) {
            return res;
        }

        await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
};
